import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";

import Button from "components/common/Button";
import Divider from "components/common/Divider";
import TextInput from "components/common/InputField/TextInput";
import ModalContainer, { ModalContainerProps } from "components/common/ModalContainer";
import { useAddSpaceGuests } from "hooks/spaceHooks";
import toast from "react-hot-toast";
import CustomToast from "components/common/CustomToast";
import { useParams } from "react-router-dom";
import { getTextFieldProps } from "helpers/getTextFieldProps";

type SpaceInviteGuestModalProps = { handleBack: () => void } & Omit<
    ModalContainerProps,
    "children"
>;

export default function SpaceInviteGuestModal(props: SpaceInviteGuestModalProps) {
    const { closeModal, handleBack, ...rest } = props;
    const { spaceId } = useParams();

    const [inviteGuestMutation, inviteGuestMutationResult] = useAddSpaceGuests();

    const formik = useFormik({
        initialValues: {
            guests: [],
        },
        validationSchema: yup.object({
            guests: yup.array().of(yup.string()).min(1, "guests are required"),
        }),
        onSubmit: async (values) => {
            try {
                await inviteGuestMutation(spaceId, values);
                toast.custom((t) => (
                    <CustomToast t={t} message="Guest invited successfully" type="success" />
                ));
                closeModal();
            } catch (error) {
                toast.custom((t) => <CustomToast t={t} message={error?.message} type="error" />);
            }
        },
    });

    return (
        <ModalContainer
            tailwindClassName="w-full max-w-[500px] max-h-[90vh]"
            closeModal={closeModal}
            {...rest}
        >
            <form onSubmit={formik.handleSubmit} className="w-full bg-white rounded-lg">
                <div className="px-[24px] flex justify-between py-[12px] items-center ">
                    <p className="text-lg font-rocGroteskMedium text-g-75">
                        Invite guest to join your space
                    </p>
                    <i
                        className="ri-close-fill text-xl text-gm-50 cursor-pointer"
                        onClick={closeModal}
                    ></i>
                </div>

                <Divider />

                <div className="p-[24px] space-y-2 overflow-y-scroll max-h-[270px] h-full scrollbar-hidden">
                    <TextInput
                        {...getTextFieldProps(formik, "guests")}
                        onChange={(e) => {
                            const guests = e.target.value.split(",");
                            formik.setFieldValue("guests", guests);
                        }}
                        value={formik.values.guests?.join(",").trim()}
                        name="guests"
                        placeholder="Enter email addresses"
                    />
                    <p className="text-sm text-slate-400 font-rocGroteskMedium">
                        Use comma to separate email addresses{" "}
                    </p>
                </div>

                <Divider />

                <div className="px-[24px] flex justify-end gap-2 py-[16px] items-center ">
                    <Button btnText="Go back" onClick={handleBack} btnClassname="!bg-n-20 !w-fit" />
                    <Button
                        isLoading={inviteGuestMutationResult.isLoading}
                        btnText="Invite guest"
                        type="submit"
                        btnClassname="!w-fit"
                    />
                </div>
            </form>
        </ModalContainer>
    );
}
